import { useMemo } from 'react';
import { ZodType } from 'zod';
import { useZodValidation } from './use-zod-validation';
import { useTouchedFields } from './use-touched-fields';

/**
 * Combined validation hook using Zod schemas with touch tracking
 *
 * Automatically validates data and only shows errors for fields that have been touched.
 *
 * @param schema - Zod schema to validate against
 * @param data - Data object to validate
 *
 * @returns Object with validation state and touch tracking utilities
 *
 * @example
 * ```tsx
 * const schema = z.object({
 *   email: z.string().email(),
 *   age: z.number().min(18)
 * });
 *
 * const MyComponent = () => {
 *   const [email, setEmail] = useState('');
 *   const [age, setAge] = useState(0);
 *
 *   const { isValid, errors, markFieldAsTouched } = useFormValidationWithTouch(
 *     schema,
 *     { email, age }
 *   );
 *
 *   return (
 *     <>
 *       <Field
 *         type="text"
 *         value={email}
 *         onChange={(v) => {
 *           setEmail(v);
 *           markFieldAsTouched('email');
 *         }}
 *         invalid={!!errors.email}
 *         description={errors.email}
 *       />
 *       <Field
 *         type="number"
 *         value={age}
 *         onChange={(v) => {
 *           setAge(v);
 *           markFieldAsTouched('age');
 *         }}
 *         invalid={!!errors.age}
 *         description={errors.age}
 *       />
 *     </>
 *   );
 * };
 * ```
 */
export const useFormValidationWithTouch = <T extends Record<string, unknown>>(schema: ZodType<T>, data: T) => {
  // Use existing hooks
  const { isValid, errors: allErrors, validatedData } = useZodValidation(schema, data);
  const touchTracking = useTouchedFields();

  // Filter errors to only show touched fields
  const visibleErrors = useMemo(() => {
    const errors: Partial<Record<keyof T, string>> = {};
    Object.keys(allErrors).forEach((key) => {
      if (touchTracking.isFieldTouched(key)) {
        errors[key as keyof T] = allErrors[key as keyof T];
      }
    });
    return errors;
  }, [allErrors, touchTracking.touchedFields]);

  // Return combined API
  return {
    /** Whether the data is valid (regardless of touched state) */
    isValid,

    /** All validation errors (regardless of touched state) */
    allErrors,

    /** Validation errors filtered to only touched fields */
    errors: visibleErrors,

    /** Validated and typed data (only available if isValid is true) */
    validatedData,

    /** Mark a field as touched */
    markFieldAsTouched: touchTracking.markFieldAsTouched,

    /** Check if a field is touched */
    isFieldTouched: touchTracking.isFieldTouched,

    /** Clear all touched fields */
    clearTouchedFields: touchTracking.clearTouchedFields,

    /** Mark multiple fields as touched */
    markMultipleFieldsAsTouched: touchTracking.markMultipleFieldsAsTouched,

    /** Get all touched fields */
    getTouchedFields: touchTracking.getTouchedFields,

    /** Set of touched field names */
    touchedFields: touchTracking.touchedFields,
  };
};

/**
 * Type helper to infer the type from a Zod schema
 *
 * @example
 * ```tsx
 * const schema = z.object({ name: z.string() });
 * type FormData = InferSchema<typeof schema>; // { name: string }
 * ```
 */
export type InferSchema<T> = T extends ZodType<infer U> ? U : never;
